import { useEffect, useRef, useState } from 'react';

import { POLLING_INTERVAL_MS, POLLING_MAX_DURATION_MS } from './pollingConstants';

/**
 * 첫 분석이 진행 중인 리포트를 완료될 때까지 polling 하는 훅
 *
 * - pollingCondition 이 true 인 동안만 동작하며, false 가 되거나 언마운트되면 즉시 정리한다.
 * - fetchFunc 는 react-query 의 refetch 를 기대한다. refetch 는 실패해도 reject 되지 않고
 *   isError 가 담긴 결과를 resolve 하므로, isError 를 실패 조건으로 직접 검사한다.
 * - 에러 응답 또는 최대 지속 시간 초과 시 isPollingFailed 를 true 로 두고 중단한다.
 * - sessionKey 가 바뀌면 (예: 다른 인플루언서 리포트로 전환) 조건이 그대로여도 새 세션을 시작한다.
 *
 * @param pollingCondition polling 조건
 * @param fetchFunc 리포트를 다시 가져오는 함수 (react-query refetch)
 * @param sessionKey polling 대상을 식별하는 키. 바뀌면 진행 중인 세션을 정리하고 새로 시작한다.
 * @returns {isLoading, isPollingFailed}
 */
const usePollingReport = (
	pollingCondition: boolean,
	fetchFunc: () => Promise<any>,
	sessionKey?: string,
) => {
	// 매 렌더마다 최신 함수를 참조해 interval 콜백이 stale closure 를 잡지 않도록 한다.
	const fetchFuncRef = useRef(fetchFunc);
	fetchFuncRef.current = fetchFunc;

	const [isLoading, setIsLoading] = useState(false);
	const [isPollingFailed, setIsPollingFailed] = useState(false);

	useEffect(() => {
		if (!pollingCondition) {
			setIsLoading(false);
			setIsPollingFailed(false);
			return;
		}

		let isActive = true;
		let intervalId: ReturnType<typeof setInterval> | null = null;
		let timeoutId: ReturnType<typeof setTimeout> | null = null;

		const stop = () => {
			isActive = false;
			if (intervalId !== null) clearInterval(intervalId);
			if (timeoutId !== null) clearTimeout(timeoutId);
			intervalId = null;
			timeoutId = null;
		};

		const finish = (failed: boolean) => {
			if (!isActive) return;
			stop();
			setIsLoading(false);
			setIsPollingFailed(failed);
		};

		setIsLoading(true);
		setIsPollingFailed(false);

		intervalId = setInterval(async () => {
			try {
				const res = await fetchFuncRef.current();
				if (!isActive) return;

				if (res?.isError) {
					finish(true);
					return;
				}
				if (res?.data?.isCompleted === true) {
					finish(false);
				}
			} catch {
				finish(true);
			}
		}, POLLING_INTERVAL_MS);

		timeoutId = setTimeout(() => finish(true), POLLING_MAX_DURATION_MS);

		return stop;
	}, [pollingCondition, sessionKey]);

	return { isLoading, isPollingFailed };
};

export default usePollingReport;
